import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, BookOpen, Map, Info, Menu, X, Languages, ShoppingBag, Stethoscope, MapPin, Headphones, GraduationCap, LogIn, LogOut, User } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_tibb-nabawi-db/artifacts/wvm4vikx_Screenshot_20260708_041833_com_whatsapp_w4b_MediaViewActivity.jpg";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const { t, setLanguage, lang, supported } = useLanguage();
  const { user, login, logout } = useAuth();
  const nav = useNavigate();
  const goSignin = () => nav("/login");
  const langLabels = { en: "English", ar: "العربية", ur: "اردو", de: "Deutsch", fr: "Français" };

  const links = [
    { to: "/", key: "home", icon: BookOpen },
    { to: "/search", key: "ai_search", icon: Search },
    { to: "/academy", key: "academy", icon: GraduationCap },
    { to: "/ruqyah", key: "ruqyah", icon: Headphones },
    { to: "/hijama-atlas", key: "hijama_atlas", icon: Map },
    { to: "/services", key: "services", icon: Stethoscope },
    { to: "/products", key: "products", icon: ShoppingBag },
    { to: "/contact", key: "contact", icon: MapPin },
  ];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 glass border-b border-forest/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-6">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src={LOGO_URL}
            alt="Islamic Cure & Care"
            className="w-12 h-12 sm:w-14 sm:h-14 object-contain transition-transform group-hover:scale-105"
          />
          <div className="hidden sm:block">
            <div className="font-heading text-xl text-forest font-semibold leading-none">
              {lang === "ar" ? "العلاج والرعاية الإسلامية" : "Islamic Cure & Care"}
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-gold mt-1">
              {t("tagline")}
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map(({ to, key, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`nav-${key}`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                  isActive
                    ? "bg-forest text-sand"
                    : "text-mute hover:text-forest hover:bg-cream"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {t(key)}
            </NavLink>
          ))}
          <div className="relative ml-2">
            <button
              data-testid="lang-toggle"
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm text-forest hover:bg-cream transition border border-forest/10"
              aria-label="Change language"
            >
              <Languages className="w-4 h-4" />
              <span className="font-medium">{langLabels[lang]}</span>
            </button>
            {langOpen && (
              <div
                data-testid="lang-menu"
                className="absolute right-0 mt-2 min-w-[140px] clay-surface rounded-2xl py-1 z-50"
                onMouseLeave={() => setLangOpen(false)}
              >
                {supported.map((l) => (
                  <button
                    key={l}
                    data-testid={`lang-option-${l}`}
                    onClick={() => { setLanguage(l); setLangOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-sm transition ${
                      lang === l ? "bg-forest text-sand" : "text-mute hover:bg-cream"
                    }`}
                  >
                    {langLabels[l]}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sign in / user menu */}
          {!user ? (
            <button
              data-testid="signin-btn"
              onClick={goSignin}
              className="ml-1 flex items-center gap-1.5 px-3 py-2 rounded-full text-sm bg-forest text-sand hover:bg-sage transition"
            >
              <LogIn className="w-4 h-4" />
              <span className="font-medium">{t("sign_in")}</span>
            </button>
          ) : (
            <div className="relative ml-1">
              <button
                data-testid="user-menu-btn"
                onClick={() => setUserOpen(!userOpen)}
                className="flex items-center gap-1.5 px-2 py-1 rounded-full hover:bg-cream transition"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <User className="w-5 h-5 text-forest" />
                )}
              </button>
              {userOpen && (
                <div
                  className="absolute right-0 mt-2 min-w-[180px] clay-surface rounded-2xl py-1 z-50"
                  onMouseLeave={() => setUserOpen(false)}
                >
                  <div className="px-4 py-2 text-xs text-mute border-b border-forest/10 truncate">{user.email}</div>
                  <button
                    data-testid="signout-btn"
                    onClick={() => { logout(); setUserOpen(false); }}
                    className="w-full text-left px-4 py-2 text-sm text-forest hover:bg-cream flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" /> {t("sign_out")}
                  </button>
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="flex items-center gap-2 md:hidden">
          <select
            data-testid="mobile-lang-toggle"
            value={lang}
            onChange={(e) => setLanguage(e.target.value)}
            className="bg-cream border border-forest/10 rounded-full px-3 py-1.5 text-xs text-forest outline-none"
            aria-label="Change language"
          >
            {supported.map((l) => (
              <option key={l} value={l}>{langLabels[l]}</option>
            ))}
          </select>
          <button
            data-testid="mobile-menu-toggle"
            className="p-2 text-forest"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="md:hidden border-t border-forest/10 bg-sand/95">
          <div className="px-4 py-4 flex flex-col gap-1">
            {links.map(({ to, key, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-all ${
                    isActive ? "bg-forest text-sand" : "text-mute hover:bg-cream"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {t(key)}
              </NavLink>
            ))}
            <div className="mt-2 pt-3 border-t border-forest/10">
              {!user ? (
                <button
                  data-testid="mobile-signin-btn"
                  onClick={() => { setOpen(false); nav("/login"); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm bg-forest text-sand"
                >
                  <LogIn className="w-4 h-4" />
                  <span className="font-medium">{t("sign_in")}</span>
                </button>
              ) : (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-3 px-4 py-2">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <User className="w-5 h-5 text-forest" />
                    )}
                    <span className="text-xs text-mute truncate">{user.email}</span>
                  </div>
                  <button
                    data-testid="mobile-signout-btn"
                    onClick={() => { setOpen(false); logout(); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm text-forest hover:bg-cream"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="font-medium">{t("sign_out")}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
